import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { ChevronLeft } from '@gravity-ui/icons';
import { countRu, LINKED_EVENT_FORMS } from '../../../utils/pluralRu';
import { getOperationalIssue } from '../../../services/operationalIssues';
import { describeHttpError } from '../../../utils/apiError';
import { APP_EVENTS, useAppEvent } from '../../../utils/appEvents';
import { formatInTimezone } from '../../../utils/formatInTimezone';
import { formatPercent, buildEventsDrilldownUrl } from '../summaryFormatters';
import {
  describeBlockedReason, ARCHIVE_STATE_LABELS, OCCURRENCE_LABELS,
  describeNotTracked, describeRecurrenceCriteria, pluralDays, describeIssueTerm,
} from './issueConstants';
import IssueTermHelp from './IssueTermHelp';
import IssueBadges from './IssueBadges';

const isCanceled = (error) => error?.code === 'ERR_CANCELED' || error?.name === 'CanceledError';

/**
 * ТЗ №2 §8: detail-панель. explanation — главный authoritative текст;
 * N — из criteria.window_days; supporting_events ≤50 + total. 404 —
 * «не найдена или недоступна» без раскрытия ACL (§9). При быстрой смене
 * issue_id старый ответ игнорируется (§11).
 *
 * ТЗ v4 §35 (FE-FIX32): occurrence_state, номер эпизода и его границы —
 * отдельная ось поверх неизменного lifecycle. Переходы эпизодов, severity и
 * trend считает backend; здесь только подписи и форматирование дат в
 * display_timezone проблемы, чтобы timezone браузера не менял смысл.
 */
const IssueDetailDrawer = ({ issueId, onClose }) => {
  const [state, setState] = useState({ status: 'loading', data: null, message: '' });
  const seqRef = useRef(0);
  // Правило проблемы сменило категорию — карточка перечитывается: в business
  // она скрывается (404 «не найдена или недоступна»), при возврате видна снова.
  const [reloadToken, setReloadToken] = useState(0);
  const reload = useCallback(() => setReloadToken((token) => token + 1), []);
  useAppEvent(APP_EVENTS.RULE_CATEGORY_CHANGED, reload);

  useEffect(() => {
    const seq = ++seqRef.current;
    const controller = new AbortController();
    setState({ status: 'loading', data: null, message: '' });
    getOperationalIssue(issueId, { signal: controller.signal })
      .then((data) => {
        if (seq === seqRef.current) setState({ status: 'ready', data, message: '' });
      })
      .catch((error) => {
        if (seq !== seqRef.current || isCanceled(error)) return;
        const message = error?.response?.status === 404
          ? 'Проблема не найдена или недоступна'
          : describeHttpError(error, 'Не удалось загрузить проблему');
        setState({ status: 'error', data: null, message });
      });
    return () => controller.abort();
  }, [issueId, reloadToken]);

  useEffect(() => {
    const reason = state.data?.resolution_blocked_reason;
    if (reason && !describeBlockedReason(reason).known) {
      console.warn('operational-issues: unknown resolution_blocked_reason', reason);
    }
  }, [state.data]);

  useEffect(() => {
    const onKey = (event) => { if (event.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const detail = state.data;
  const windowDays = detail?.criteria?.window_days;
  const blocked = describeBlockedReason(detail?.resolution_blocked_reason);
  const drilldownHref = detail ? buildEventsDrilldownUrl(detail.drilldown) : null;
  const events = detail?.supporting_events || [];
  const recurrence = describeRecurrenceCriteria(detail?.criteria);
  const notTracked = detail?.tracking_state === 'not_tracked'
    ? describeNotTracked(detail.explanation, detail.tracking_reason)
    : null;
  const occurrence = detail ? OCCURRENCE_LABELS[detail.occurrence_state] : null;
  const episodeNo = detail?.episode_no != null ? `Эпизод №${detail.episode_no}` : null;

  return (
    <div className="oi-drawer-backdrop" onClick={onClose}>
      <aside
        className="oi-drawer"
        role="dialog"
        aria-label="Системная проблема"
        onClick={(event) => event.stopPropagation()}
      >
        <button type="button" className="sum-btn sum-btn--icon oi-drawer-back" onClick={onClose}>
          <ChevronLeft width={16} height={16} aria-hidden />
          Назад
        </button>

        {state.status === 'loading' && <p className="sum-muted" role="status">Загрузка…</p>}
        {state.status === 'error' && <p className="oi-error" role="alert">{state.message}</p>}

        {detail && (
          <div className="oi-detail">
            <IssueBadges
              status={detail.status}
              severity={detail.severity}
              trend={detail.trend}
              archiveState={detail.archive_state}
            />
            <h2 className="oi-detail-title">{detail.rule_name}</h2>
            <p className="oi-detail-scope">{detail.camera_name} · {detail.object_name}</p>
            <p className="oi-detail-scope">{detail.zone_name || 'Общая область контроля'}</p>

            {/* FE-CORR-07 FE-006: один блок; explanation, начатый теми же
                словами, в него уже вошёл и ниже не повторяется. */}
            {notTracked && (
              <p className="oi-warning" role="note">
                <IssueTermHelp
                  term={describeIssueTerm('tracking_state', 'not_tracked')}
                  className="oi-term-trigger--text"
                >
                  <b>Больше не отслеживается</b>
                </IssueTermHelp>. {notTracked.reason}.
                Статус и история проблемы сохраняются.
              </p>
            )}
            {detail.archive_state && detail.archive_state !== 'active' && (
              <p className="oi-warning" role="note">
                {ARCHIVE_STATE_LABELS[detail.archive_state] || detail.archive_state}.
                Связанные события и доказательства не затронуты.
              </p>
            )}

            {detail.explanation && !notTracked?.consumesExplanation && (
              <p className="oi-detail-explanation">{detail.explanation}</p>
            )}

            <div className="oi-detail-counts">
              <span><b>{detail.current_window_count}</b> за последние {pluralDays(windowDays)}</span>
              <span><b>{detail.previous_window_count}</b> за предыдущие {pluralDays(windowDays)}</span>
            </div>
            <p>Непрерывность контроля {formatPercent(detail.control_continuity_percent)}</p>
            {blocked && <p className="oi-warning" role="note">{blocked.text}</p>}

            <dl className="oi-detail-dates">
              <dt>Впервые обнаружено</dt>
              <dd>{formatInTimezone(detail.first_detected_at, detail.display_timezone)}</dd>
              <dt>Последняя оценка</dt>
              <dd>{formatInTimezone(detail.last_detected_at, detail.display_timezone)}</dd>
              <dt>Последнее нарушение</dt>
              <dd>{formatInTimezone(detail.last_occurrence_at, detail.display_timezone)}</dd>
              {detail.resolved_at && (
                <>
                  <dt>Решена</dt>
                  <dd>{formatInTimezone(detail.resolved_at, detail.display_timezone)}</dd>
                </>
              )}
              {detail.reopened_at && (
                <>
                  <dt>Возобновилась</dt>
                  <dd>{formatInTimezone(detail.reopened_at, detail.display_timezone)}</dd>
                </>
              )}
              {detail.episode_started_at && (
                <>
                  <dt>Эпизод начался</dt>
                  <dd>{formatInTimezone(detail.episode_started_at, detail.display_timezone)}</dd>
                </>
              )}
              {detail.last_qualified_at && (
                <>
                  <dt>Последнее срабатывание порога</dt>
                  <dd>{formatInTimezone(detail.last_qualified_at, detail.display_timezone)}</dd>
                </>
              )}
              {detail.archived_at && (
                <>
                  <dt>В архиве с</dt>
                  <dd>{formatInTimezone(detail.archived_at, detail.display_timezone)}</dd>
                </>
              )}
              <dt>Часовой пояс</dt>
              <dd>{detail.display_timezone}</dd>
            </dl>

            {/* Повторение — строкой, а не вторым бейджем состояния (FE-005). */}
            {/* ТЗ V5 №15: у повторения своё пояснение, отличное от динамики «Новая». */}
            {(episodeNo || occurrence) && (
              <p className="oi-detail-episode">
                {episodeNo}
                {episodeNo && occurrence && ' · '}
                {occurrence && (
                  <IssueTermHelp
                    term={describeIssueTerm('occurrence_state', detail.occurrence_state)}
                    className="oi-term-trigger--text"
                  >
                    {occurrence}
                  </IssueTermHelp>
                )}
              </p>
            )}
            {recurrence && <p className="oi-detail-recurrence">{recurrence}</p>}

            {detail.supporting_events_total != null && (
              <p>{countRu(detail.supporting_events_total, LINKED_EVENT_FORMS)}</p>
            )}
            {drilldownHref && <Link className="sum-link" to={drilldownHref}>Посмотреть связанные события</Link>}

            <h3 className="oi-detail-sub">Связанные события</h3>
            {events.length === 0
              ? <p className="sum-muted">Связанных событий нет</p>
              : (
                <ul className="oi-events">
                  {events.map((event) => (
                    <li key={event.event_id}>
                      <span>{formatInTimezone(event.confirmed_at, detail.display_timezone)}</span>
                      <Link className="sum-link" to={`/violations/${event.event_id}`}>Открыть событие</Link>
                    </li>
                  ))}
                </ul>
              )}
            <p className="sum-muted">Показано {events.length} из {detail.supporting_events_total}</p>
          </div>
        )}
      </aside>
    </div>
  );
};

export default IssueDetailDrawer;
